import React, { useState, useEffect } from 'react';
import { makeStyles } from "@mui/styles";
import { Select, Dialog, DialogActions, DialogContent, DialogTitle, Box, Button, MenuItem, FormControl, Step, Stepper, StepContent, StepLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import FlagstoneTheme from '../../../Config/flagstoneTheme';
import { X, LoaderCircle } from 'lucide-react';

import { dataService } from '../../../Services/data/data.service';

export default function PreviewDialog(props) {

    const classes = useStyles();
    const { t } = useTranslation();
    const [printerSelected, setPrinterSelected] = useState(0);

    useEffect(() => {
        // set printer to first from printer list
        if (props.printers !== null && props.printers !== undefined && props.printers.length > 0) {
            props.setPrinterSelected(props.printers[0].Id);
        }
    }, [props.printers]);

    const handleChange = (e) => {
        props.setPrinterSelected(e.target.value);
    }

    const handlePrint = () => {
        if (!props.submitting) {
            props.setSubmitting(true);
            props.action(printerSelected);
        }
    }

    return (

        <Dialog className={classes.dialog}
            open={props.dialogOpen}
            onClose={props.closeDialog}

            classes={{ paper: classes.dialogPaper }}

            aria-labelledby="draggable-dialog-title"
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {t('Reprint label')}
                </DialogTitle>
                <button onClick={() => props.closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <div className={classes.dialog__printPreview}>
                        <img src={"data:image/png;base64," + props.printPreview} alt="Preview" style={{ width: '95%' }} />
                    </div>
                    <div className={classes.dialog__printSetting}>
                        <label className={classes.dialog__printSettingsLabel}>{t("Select a printer")}</label>
                        <Select
                            className={classes.dialog__printSettingsField}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={props.printerSelected}
                            disabled
                            onChange={handleChange}
                        >
                            {props.printers.map((printer, index) => {
                                return <MenuItem value={printer.Id} key={"printer_" + index}><b style={{ paddingRight: '5px' }}>{printer.Code}:</b> {printer.Name}</MenuItem>
                            })}
                        </Select>
                    </div>
                </div>
            </DialogContent>
            <DialogActions className={classes.dialog__buttonContainer}>
                <Button className={classes.dialog__button__secondary} onClick={props.closeDialog}>
                    {t("Close")}
                </Button>
                <button className={`${classes.dialog__button} ${props.submitting ? classes.dialog__button_loading : ""}`} disabled={props.printerSelected === null || props.printerSelected === 0} onClick={() => handlePrint()}>
                    {props.submitting && <div className={classes.button__loader}><LoaderCircle className={classes.button__loader__spinner} /></div>}
                    {t("Print label")}
                </button>
            </DialogActions>
        </Dialog>
    );
}

const useStyles = makeStyles(() => ({

    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        minWidth: '720px',
        width: '720px !important',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'backgroundColor 0.2s, color 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },

    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        display: "flex !important",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',

    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',

        margin: '0px !important',
        marginBottom: "16px !important"
    },

    dialog__printPreview: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px !important',
    },

    dialog__printSetting: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },

    dialog__printSettingsLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#0C0E29',
    },

    dialog__printSettingsField: {
        width: '100%',
        height: '40px !important',
        padding: '0px !important',
    },

    dialog__buttonContainer: {
        width: '100%',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between !important',

        padding: '16px !important',
        paddingTop: '0px !important',
        margin: '0px !important',
    },

    dialog__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "none !important",
        borderRadius: "3px !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: 'none !important',
        position: 'relative !important',

        marginLeft: 'auto !important',

        transition: 'all 0.2s !important',

        '&:hover': {
            backgroundColor: "#3B3AF1 !important",
            color: "white !important",
            cursor: "pointer !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    dialog__button_loading: {
        backgroundColor: "#C3D3FF !important",
        color: "#C3D3FF !important",

        '&:hover': {
            backgroundColor: "#C3D3FF !important",
            color: "#C3D3FF !important",
            cursor: "default !important",
        },
    },

    button__loader: {
        width: "24px",
        height: "24px",
        color: FlagstoneTheme.palette.primary.main,

        position: "absolute",
        display: "block",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    },

    button__loader__spinner: {
        width: "100%",
        height: "100%",

        animation: "spin 1s linear infinite",
    },

    '@global': {
        '@keyframes spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    },

    dialog__button__secondary: {
        color: FlagstoneTheme.palette.primary.main + " !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: "white !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: 'capitalize !important',

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    stepButton: {
        zIndex: 1,
        color: '#fff',
        width: 50,
        height: 50,
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    okButton: {
        color: "white !important",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "bold !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            outline: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            outline: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    action2Button: {
        color: FlagstoneTheme.palette.primary.main + ' !important',
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "bold !important",
        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: 'white !important'
        },
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        marginRight: '15px !important',
        width: "max-content",
        maxWidth: "200px",
    },
    dialogTitle: {
        color: "#000000",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        textAlign: "center",
        '& h2': {
            fontSize: "32px !important",
        },
        paddingTop: "20px",
    }, resultText: {
        fontSize: '13px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        color: FlagstoneTheme.palette.error.main,
        fontWeight: '500',
        height: '25px',
        width: '100%'
    },
    dialogContext: {
        color: "#000000",
        fontSize: "18px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        textAlign: "center"
    }, uiTextField: {
        backgroundColor: "white",
        marginBottom: "10px !important"
    },
}));
